'use client'

// The town dressed for Halloween, the weekend of 31 Oct and 1 Nov 2026 (Sam, 31 Oct: "go to town on this it should be
// noticeably different! Use instancing wherever possible. Bare in mind on Sunday night we will remove these again").
//
// Drawn only while the town says it is on (lib/world/halloween.js, the town's switch is sim world/Halloween.js), and
// nothing is fetched until then: halloween.glb and the webs come down with the first frame of the weekend and never
// after it.
//
// halloween.glb is heyheythere's PSX Halloween Props Free (CC BY 4.0), placed in Kiwi_world.blend by
// scripts/blender/halloween.py: every lantern, pumpkin, gourd, candle, headstone, spider and wall web a linked copy, so
// each kind is one GPU-instanced draw (69 lanterns are three draws: the pumpkin, its lid, its face). World coordinates,
// rendered at identity. Inside it:
//   jack_o_lantern_glow   the carved face, drawn unlit so it reads as candlelight, each one flickering on its own
//   spider_hanging_spider the spiders, each bobbing on its thread
//   cauldron_bubble_*     the cauldron's bubbles, rising and falling
//   costume_witch_hat     his hat, never drawn where it stands (the bird wears it, ServerNPCBridge)
//
// And the webs, spun here in their hundreds from the frames found in the blend (lib/world/halloweenWebs.js): one
// line draw, moving a little in the air.

import { Suspense, useEffect, useMemo, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { materialColor, vec3, float, sin, cos, time, instanceIndex, hash, positionLocal, positionWorld, cameraPosition, smoothstep, attribute, mix } from 'three/tsl'
import { useModel } from '../hooks/useModel'
import { useSceneNodeMaterials, toNode } from '../hooks/useSceneNodeMaterials'
import { ditherMaterial } from '../effects/ditherRegistry'
import { useNPCInstancedAnimations } from '../npc/NPCInstancedAnimationProvider'
import { mood } from '../../../lib/world/mood'
import useGameStore from '../../../stores/gameStore'
import { halloweenShown } from '../../../lib/world/halloween'
import { spinWebs } from '../../../lib/world/halloweenWebs'

const MODEL = '/models/halloween.glb'
const WEBS = '/models/halloween-webs.json'

export function Halloween() {
  const on = useGameStore((s) => halloweenShown(s.environment))
  if (!on) return null
  // its own boundary: it arrives mid-session, and a load that suspended the town's would blank the whole town for it
  // (mainCanvas.jsx, the Suspense there)
  return (
    <Suspense fallback={null}>
      <HalloweenProps />
      <HalloweenWebs />
    </Suspense>
  )
}

// a number per instance, 0..1, the same every frame: what keeps each lantern's flicker and each spider's bob its own
const own = (k) => hash(instanceIndex.add(k))

// a fresh lit material like the file's (clone() on a node material drops the map: NodeMaterial.copy is Material's)
function like(src) {
  const m = new THREE.MeshStandardNodeMaterial()
  m.map = src.map || null
  m.vertexColors = src.vertexColors
  m.color = src.color?.clone() ?? new THREE.Color('white')
  m.roughness = src.roughness ?? 0.95
  m.metalness = 0
  return m
}

function HalloweenProps() {
  const { scene } = useModel(MODEL)
  useSceneNodeMaterials(scene, { roughness: 0.95 })

  useEffect(() => {
    if (!scene) return
    const made = new Map()
    const once = (key, build) => { if (!made.has(key)) made.set(key, build()); return made.get(key) }
    scene.traverse((o) => {
      if (o.name === 'costume_witch_hat') { o.visible = false; return }
      if (!o.isMesh) return
      // the material the file gave it, kept: this runs twice in development, and the second time round o.material is
      // already the one made here
      o.userData.hwSource ??= Array.isArray(o.material) ? o.material[0] : o.material
      const src = o.userData.hwSource
      // the PS1 texels: the atlas is 512 and meant to be seen square
      if (src?.map) { src.map.magFilter = THREE.NearestFilter; src.map.needsUpdate = true }

      if (/jack_o_lantern_glow/.test(o.name)) {
        o.material = once('glow', () => {
          const m = new THREE.MeshBasicNodeMaterial({ map: src.map || null, vertexColors: src.vertexColors })
          // two slow wobbles and a quick one, out of step lantern to lantern; brighter once it is dark
          const t = time.mul(1.0)
          const flick = float(0.78)
            .add(sin(t.mul(9.0).add(own(1).mul(40.0))).mul(0.09))
            .add(sin(t.mul(23.0).add(own(2).mul(17.0))).mul(0.05))
            .add(sin(t.mul(3.1).add(own(3).mul(9.0))).mul(0.06))
          const night = mix(float(1.0), float(1.55), mood.nightness)
          // (the atlas and the vertex colours as the file has them, warmed, then the candle on top)
          m.colorNode = materialColor.rgb.mul(vec3(1.35, 1.05, 0.62)).mul(flick).mul(night)
          return m
        })
        o.castShadow = false
        o.receiveShadow = false
        return
      }
      if (/spider_hanging_spider/.test(o.name)) {
        o.material = once('spider', () => {
          const m = like(src)
          // up and down its thread, slowly, each on its own clock (the model is y up, so is the bob)
          m.positionNode = positionLocal.add(vec3(0, sin(time.mul(float(0.7).add(own(4).mul(0.6))).add(own(5).mul(6.28))).mul(0.12).sub(0.06), 0))
          return m
        })
        return
      }
      if (/cauldron_bubble/.test(o.name)) {
        const k = Number(o.name.match(/(\d)/)?.[1] || 1)
        o.material = once('bubble' + k, () => {
          const m = like(src)
          m.positionNode = positionLocal.add(vec3(0, sin(time.mul(1.6 + k * 0.37).add(k * 1.7)).mul(0.012), 0))
          return m
        })
      }
    })
  }, [scene])

  return <primitive object={scene} />
}

function HalloweenWebs() {
  const [frames, setFrames] = useState(null)
  useEffect(() => {
    let alive = true
    fetch(WEBS).then((r) => r.json()).then((j) => { if (alive) setFrames(j.webs || []) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const lines = useMemo(() => {
    if (!frames?.length) return null
    const { positions, free, phase } = spinWebs(frames)
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    g.setAttribute('free', new THREE.BufferAttribute(free, 1))
    g.setAttribute('phase', new THREE.BufferAttribute(phase, 1))
    g.computeBoundingSphere()
    const m = new THREE.LineBasicNodeMaterial({ transparent: true, depthWrite: false })
    const f = attribute('free', 'float')
    const ph = attribute('phase', 'float')
    // what moves is the middle of each thread; the anchors hold
    m.positionNode = positionLocal.add(vec3(
      sin(time.mul(1.3).add(ph)).mul(0.035),
      sin(time.mul(0.9).add(ph.mul(1.7))).mul(0.02),
      cos(time.mul(1.1).add(ph.mul(0.6))).mul(0.035),
    ).mul(f))
    // silk is lit by what lights the town, not by itself: a soft grey by day, and at night the town's own dim blue (drawn
    // unlit and pale, the webs glowed in the dark, Sam, 31 Oct: "they don't take on the scenes lighting they stand out").
    // and it is only seen near: past a dozen metres it thins out and by forty it is gone, so across the street there is
    // no web to read as a line, and up close there is a web ("they look like thick lines")
    const near = float(1).sub(smoothstep(float(12), float(38), positionWorld.distance(cameraPosition)))
    m.colorNode = mix(vec3(0.7, 0.7, 0.67), vec3(0.2, 0.22, 0.32), mood.nightness).mul(float(1).sub(mood.overcast.mul(0.25)))
    m.opacityNode = float(0.62).sub(f.mul(0.2)).mul(near)
    ditherMaterial(m)
    const l = new THREE.LineSegments(g, m)
    l.frustumCulled = false
    l.renderOrder = 2
    return l
  }, [frames])

  useEffect(() => () => { lines?.geometry.dispose(); lines?.material.dispose() }, [lines])

  return lines ? <primitive object={lines} /> : null
}

// ── his hat ─────────────────────────────────────────────────────────────
// Sam, 31 Oct: "lets try the witches hat from the pack, see if we can get that fitting on his head well". It rides his
// head bone, so it nods when he pecks and goes down with him when he sits: his instance's matrix times where his head
// is now, against where it is at rest (the provider's boneWorld and boneRest), times where the hat sits on the crown
// at rest. Fitted against bird.glb's own vertices: his head is longer than it is wide (at y 2.04 it runs x within 0.25
// of the middle and z 0.27 to 0.88, his beak on along +z; the top is at 2.18), and the pack's hat is round, its crown
// 0.13 across inside at the band. Tipped back and seated low, the back of his head came out through it (Sam, 31 Oct:
// "the hat also clips through the back of his head": 450 of the 727 points of his head above the brim were outside
// the hat, by up to 17 cm). Seated at 2.10 where the head has narrowed, centred on it there, and drawn a little longer
// than wide, every point of his head above the brim is inside the hat with 5 cm to spare. It is big on him, which it
// should be.
const PINO = 'npc_victor'
const HAT_ON = { y: 2.1, z: 0.55, scale: [2.1, 2.0, 2.3], back: 0, askew: 0.06 }

export function HalloweenHat() {
  const on = useGameStore((s) => halloweenShown(s.environment))
  if (!on) return null
  return (
    <Suspense fallback={null}>
      <Hat />
    </Suspense>
  )
}

function Hat() {
  const { scene } = useModel(MODEL)
  const instanced = useNPCInstancedAnimations()

  const hat = useMemo(() => {
    const src = scene?.getObjectByName('costume_witch_hat')
    if (!src?.isMesh) return null
    const mat = toNode(Array.isArray(src.material) ? src.material[0] : src.material, new Map(), 0.95)
    if (mat?.map) { mat.map.magFilter = THREE.NearestFilter; mat.map.needsUpdate = true }
    const m = new THREE.Mesh(src.geometry, mat)
    m.name = 'his_witch_hat'
    m.matrixAutoUpdate = false
    m.castShadow = true
    m.frustumCulled = false
    return m
  }, [scene])

  // where the hat sits on him at rest, against his head bone at rest: worked out once
  const fit = useMemo(() => {
    const onHead = new THREE.Matrix4().compose(
      new THREE.Vector3(0, HAT_ON.y, HAT_ON.z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(HAT_ON.back, 0, HAT_ON.askew)),
      new THREE.Vector3(...HAT_ON.scale),
    )
    return { onHead, rest: null, now: new THREE.Matrix4() }
  }, [])

  useFrame(() => {
    if (!hat || !instanced?.boneWorld) return
    if (!fit.rest) {
      const rest = new THREE.Matrix4()
      if (!instanced.boneRest('head', rest)) return
      fit.rest = rest.invert().multiply(fit.onHead)
    }
    const ok = instanced.boneWorld(PINO, 'head', fit.now)
    hat.visible = ok
    if (ok) hat.matrix.copy(fit.now).multiply(fit.rest)
  })

  return hat ? <primitive object={hat} /> : null
}

export default Halloween
